import { computeForMonth, convertMinor, isPaid, type Pence, type VulpariNode } from "@vulpari/core";
import { useMemo, useRef, useState } from "react";

import { formatMoney, formatMonthLabel, formatMonthShort } from "../lib/format.js";
import { useStore } from "../store.js";
import { Icon, type IconName } from "./Icon.js";

interface Card {
  key: string;
  node: VulpariNode; // node to open on the canvas when clicked
  label: string;
  amount: number; // primary-currency minor units, positive magnitude
  direction: "in" | "out";
  transfer?: boolean; // a move between your own accounts, drawn as a wire
}

interface Station {
  key: string;
  label: string;
  cards: Card[];
  left: number;
  tone: "in" | "out";
}

interface Column {
  key: string;
  label: string;
  day: number | null;
}

interface Lane {
  account: VulpariNode;
  cards: Map<string, Card[]>; // by column key
  inflow: number;
  outflow: number;
  left: number;
}

const TYPE_ICON: Record<VulpariNode["type"], IconName> = {
  account: "bank",
  card: "credit-card",
  income: "money-wavy",
  bill: "receipt",
  oneoff: "calendar-dot",
  pot: "piggy-bank",
  external: "storefront",
};

const TYPE_COLOUR: Record<VulpariNode["type"], string> = {
  account: "var(--colour-neutral)",
  card: "var(--colour-warning)",
  income: "var(--colour-positive)",
  bill: "var(--colour-neutral)",
  oneoff: "var(--colour-accent)",
  pot: "var(--colour-positive)",
  external: "var(--colour-text-muted)",
};

const ICON_COLOUR: Record<VulpariNode["type"], string> = {
  account: "text-neutral",
  card: "text-warning",
  income: "text-positive",
  bill: "text-neutral",
  oneoff: "text-accent",
  pot: "text-positive",
  external: "text-ink-muted",
};

/** Node types that can be ticked off as paid. */
const PAYABLE = new Set<VulpariNode["type"]>(["bill", "card", "oneoff", "pot", "external"]);

/** The day of the month an event falls on, or null for an undated recurring one. */
function eventDay(node: VulpariNode): number | null {
  if (node.type === "bill" || node.type === "card") {
    return node.meta.dueDay;
  }
  if (node.type === "oneoff") {
    return Number(node.meta.dueDate.slice(8, 10));
  }
  return null;
}

export function Timeline() {
  const nodes = useStore((state) => state.nodes);
  const flows = useStore((state) => state.flows);
  const context = useStore((state) => state.context);
  const selectNode = useStore((state) => state.selectNode);
  const setView = useStore((state) => state.setView);
  const ledger = useStore((state) =>
    state.ledgers.find((entry) => entry.id === state.currentLedgerId),
  );

  const currency = context.primaryCurrency ?? "GBP";
  const month = context.month;
  const startMonth = ledger?.startMonth ?? month;

  const [byAccount, setByAccount] = useState(false);

  const { result } = useMemo(
    () =>
      computeForMonth({
        nodes,
        flows,
        startMonth,
        month,
        primaryCurrency: currency,
        rates: context.rates,
      }),
    [nodes, flows, startMonth, month, currency, context.rates],
  );

  // The month's moves, with amounts in the primary currency and their nodes.
  const moves = useMemo(() => {
    const nodeById = new Map<string, VulpariNode>(nodes.map((node) => [node.id, node]));
    const list: { source: VulpariNode; target: VulpariNode; amount: number; flowId: string }[] = [];
    for (const move of result.moves) {
      const source = nodeById.get(move.fromNodeId);
      const target = nodeById.get(move.toNodeId);
      if (!source || !target) {
        continue;
      }
      const amount = convertMinor(move.amount as Pence, source.currency, currency, context.rates);
      list.push({ source, target, amount, flowId: move.flowId });
    }
    return list;
  }, [nodes, result.moves, currency, context.rates]);

  // Pooled view: every event on one rail, by date.
  const pooled = useMemo(() => {
    const income: Card[] = [];
    const undated: Card[] = [];
    const byDay = new Map<number, Card[]>();

    for (const move of moves) {
      if (move.source.type === "income") {
        income.push({
          key: move.flowId,
          node: move.source,
          label: move.source.name,
          amount: move.amount,
          direction: "in",
        });
        continue;
      }
      if (move.target.type === "account") {
        continue; // internal plumbing, hidden in the pooled view
      }
      const card: Card = {
        key: move.flowId,
        node: move.target,
        label: move.target.name,
        amount: move.amount,
        direction: "out",
      };
      const day = eventDay(move.target);
      if (day === null) {
        undated.push(card);
      } else {
        byDay.set(day, [...(byDay.get(day) ?? []), card]);
      }
    }

    const sum = (cards: Card[]) => cards.reduce((total, card) => total + card.amount, 0);
    const totalIn = sum(income);
    const days = [...byDay.entries()].sort((a, b) => a[0] - b[0]);

    const stations: Station[] = [];
    let running = totalIn;
    if (income.length > 0) {
      stations.push({ key: "in", label: "Pay in", cards: income, left: running, tone: "in" });
    }
    for (const [day, cards] of days) {
      running -= sum(cards);
      stations.push({
        key: `d${day}`,
        label: `${day} ${formatMonthShort(month)}`,
        cards,
        left: running,
        tone: "out",
      });
    }
    if (undated.length > 0) {
      running -= sum(undated);
      stations.push({
        key: "undated",
        label: "No set day",
        cards: undated,
        left: running,
        tone: "out",
      });
    }
    const totalOut = days.reduce((t, [, c]) => t + sum(c), 0) + sum(undated);
    // Still to pay this month: unpaid obligations.
    const remaining = moves.reduce((total, move) => {
      if (!PAYABLE.has(move.target.type) || isPaid(move.target, month)) {
        return total;
      }
      return total + move.amount;
    }, 0);
    return { stations, totalIn, totalOut, remaining };
  }, [moves, month]);

  // By-account view: one lane per account, money in landing in its account and
  // account-to-account transfers drawn as wires.
  const grouped = useMemo(() => {
    const accounts = nodes.filter((node) => node.type === "account");
    const dayKeys = new Set<number>();
    let anyUndated = false;

    const lanes: Lane[] = accounts.map((account) => {
      const cards = new Map<string, Card[]>();
      const push = (colKey: string, card: Card) =>
        cards.set(colKey, [...(cards.get(colKey) ?? []), card]);
      let inflow = 0;
      let outflow = 0;

      for (const move of moves) {
        // Money arriving in this account: pay, or a transfer from another account.
        if (move.target.id === account.id && move.source.type === "income") {
          inflow += move.amount;
          push("payin", {
            key: move.flowId,
            node: move.source,
            label: move.source.name,
            amount: move.amount,
            direction: "in",
          });
        } else if (move.target.id === account.id && move.source.type === "account") {
          inflow += move.amount;
          push("payin", {
            key: move.flowId,
            node: move.source,
            label: move.source.name,
            amount: move.amount,
            direction: "in",
            transfer: true,
          });
        }

        // Money leaving this account.
        if (move.source.id === account.id) {
          outflow += move.amount;
          if (move.target.type === "account") {
            push("payin", {
              key: move.flowId,
              node: move.target,
              label: move.target.name,
              amount: move.amount,
              direction: "out",
              transfer: true,
            });
          } else {
            const day = eventDay(move.target);
            const card: Card = {
              key: move.flowId,
              node: move.target,
              label: move.target.name,
              amount: move.amount,
              direction: "out",
            };
            if (day === null) {
              anyUndated = true;
              push("undated", card);
            } else {
              dayKeys.add(day);
              push(`d${day}`, card);
            }
          }
        }
      }

      return { account, cards, inflow, outflow, left: inflow - outflow };
    });

    const columns: Column[] = [
      { key: "payin", label: "Pay in", day: null },
      ...[...dayKeys]
        .sort((a, b) => a - b)
        .map((day) => ({ key: `d${day}`, label: `${day} ${formatMonthShort(month)}`, day })),
      ...(anyUndated ? [{ key: "undated", label: "No set day", day: null }] : []),
    ];

    return { columns, lanes: lanes.filter((lane) => lane.inflow > 0 || lane.outflow > 0) };
  }, [moves, nodes, month]);

  const left = pooled.totalIn - pooled.totalOut;

  // Click-and-drag to pan horizontally, like the canvas.
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; scrollLeft: number } | null>(null);
  const moved = useRef(false);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = scrollRef.current;
    if (!element) return;
    drag.current = { startX: event.clientX, scrollLeft: element.scrollLeft };
    moved.current = false;
    element.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = scrollRef.current;
    if (!element || !drag.current) return;
    const dx = event.clientX - drag.current.startX;
    if (Math.abs(dx) > 3) moved.current = true;
    element.scrollLeft = drag.current.scrollLeft - dx;
  };
  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    scrollRef.current?.releasePointerCapture(event.pointerId);
  };

  const open = (node: VulpariNode) => {
    if (moved.current) return;
    selectNode(node.id);
    setView("canvas");
  };

  const hasAny = byAccount ? grouped.lanes.length > 0 : pooled.stations.length > 0;

  return (
    <div className="canvas-dots flex h-full flex-col overflow-hidden bg-canvas">
      <div className="px-6 pb-2 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">Timeline</h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-muted">
              How {formatMonthLabel(month)} plays out by due date. Pay is the month's opening money;
              click a card to open it on the canvas.
            </p>
          </div>
          <div
            className="flex rounded-lg border border-node-border p-0.5"
            role="group"
            aria-label="Grouping"
          >
            {(
              [
                ["pooled", "Pooled"],
                ["account", "By account"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setByAccount(value === "account")}
                aria-pressed={byAccount === (value === "account")}
                className={`rounded-md px-3 py-1 text-sm transition-colors ${
                  byAccount === (value === "account")
                    ? "bg-accent text-canvas"
                    : "text-ink-muted hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid max-w-4xl gap-3 sm:grid-cols-4">
          <Summary label="Money in" value={formatMoney(pooled.totalIn, currency)} tone="positive" />
          <Summary label="Money out" value={formatMoney(pooled.totalOut, currency)} tone="accent" />
          <Summary
            label="Still to pay"
            value={formatMoney(pooled.remaining, currency)}
            tone={pooled.remaining > 0 ? "accent" : "positive"}
          />
          <Summary
            label="Left from pay"
            value={formatMoney(left, currency)}
            tone={left < 0 ? "warning" : "ink"}
          />
        </div>
      </div>

      {!hasAny ? (
        <p className="mx-6 rounded-xl border border-node-border bg-node-bg px-4 py-3 text-sm text-ink-muted">
          Nothing scheduled for this month yet. Wire up some income and bills to see them here.
        </p>
      ) : (
        <div
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="scroll-slim flex-1 cursor-grab select-none overflow-auto px-6 pb-8 pt-2 active:cursor-grabbing"
        >
          {byAccount ? (
            <ByAccount grouped={grouped} currency={currency} onOpen={open} />
          ) : (
            <div className="timeline-group flex min-w-max items-start">
              {pooled.stations.map((station, index) => (
                <div key={station.key} className="flex w-52 shrink-0 flex-col px-1">
                  <RailHeader
                    label={station.label}
                    tone={station.tone}
                    first={index === 0}
                    last={index === pooled.stations.length - 1}
                  />
                  <div className="mt-3 flex flex-col gap-2">
                    {station.cards.map((card) => (
                      <EventCard key={card.key} card={card} currency={currency} onOpen={open} />
                    ))}
                  </div>
                  <div className="mt-3 text-center text-xs text-ink-muted">
                    Left{" "}
                    <span className={`figure ${station.left < 0 ? "text-warning" : "text-ink"}`}>
                      {formatMoney(station.left, currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ByAccount({
  grouped,
  currency,
  onOpen,
}: {
  grouped: { columns: Column[]; lanes: Lane[] };
  currency: string;
  onOpen: (node: VulpariNode) => void;
}) {
  // Fixed column widths so the header and every lane line up, even though each
  // lane is its own separated panel.
  const gridStyle = {
    gridTemplateColumns: `13rem repeat(${grouped.columns.length}, 14rem)`,
  };
  return (
    <div className="timeline-group min-w-max">
      {/* Header: an empty corner then the date pills on the rail. */}
      <div className="grid gap-x-2 px-2" style={gridStyle}>
        <div />
        {grouped.columns.map((column, index) => (
          <RailHeader
            key={column.key}
            label={column.label}
            tone={column.key === "payin" ? "in" : "out"}
            first={index === 0}
            last={index === grouped.columns.length - 1}
          />
        ))}
      </div>

      {/* One tinted, bordered band per account, with clear spacing between. */}
      <div className="mt-3 flex flex-col gap-3">
        {grouped.lanes.map((lane) => (
          <div
            key={lane.account.id}
            className="rounded-2xl border border-node-border bg-node-bg/30 px-2 py-3"
          >
            <div className="grid items-start gap-x-2" style={gridStyle}>
              <LaneRow lane={lane} columns={grouped.columns} currency={currency} onOpen={onOpen} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LaneRow({
  lane,
  columns,
  currency,
  onOpen,
}: {
  lane: Lane;
  columns: Column[];
  currency: string;
  onOpen: (node: VulpariNode) => void;
}) {
  return (
    <>
      <div className="flex flex-col justify-center px-2">
        <div className="flex items-center gap-2">
          <Icon name="bank" size={18} className="text-neutral" />
          <p className="truncate text-sm font-semibold text-ink">{lane.account.name}</p>
        </div>
        <p className="mt-1 text-xs text-ink-muted">
          Left{" "}
          <span className={`figure ${lane.left < 0 ? "text-warning" : "text-ink"}`}>
            {formatMoney(lane.left, currency)}
          </span>
        </p>
      </div>
      {columns.map((column) => (
        <div key={column.key} className="flex flex-col gap-2">
          {(lane.cards.get(column.key) ?? []).map((card) => (
            <EventCard key={card.key} card={card} currency={currency} onOpen={onOpen} />
          ))}
        </div>
      ))}
    </>
  );
}

function RailHeader({
  label,
  tone,
  first,
  last,
}: {
  label: string;
  tone: "in" | "out";
  first: boolean;
  last: boolean;
}) {
  return (
    <div className="flex items-center">
      <div className={`h-0.5 flex-1 ${first ? "" : "timeline-rail"}`} />
      <span
        className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium ${
          tone === "in"
            ? "border-positive/40 bg-positive/10 text-ink"
            : "border-node-border bg-node-bg text-ink"
        }`}
      >
        {label}
      </span>
      <div className={`h-0.5 flex-1 ${last ? "" : "timeline-rail"}`} />
    </div>
  );
}

function EventCard({
  card,
  currency,
  onOpen,
}: {
  card: Card;
  currency: string;
  onOpen: (node: VulpariNode) => void;
}) {
  const month = useStore((state) => state.context.month);
  const togglePaid = useStore((state) => state.togglePaid);

  if (card.transfer) {
    return (
      <button
        type="button"
        onClick={() => onOpen(card.node)}
        className="flex items-center gap-2 rounded-lg border border-dashed border-node-border bg-node-bg/60 px-3 py-1.5 text-left text-xs hover:border-accent"
        title="Transfer between accounts"
      >
        <span className="text-ink-muted">{card.direction === "in" ? "←" : "→"}</span>
        <span className="min-w-0 flex-1 truncate text-ink-muted">{card.label}</span>
        <span className="figure text-ink-muted">
          {card.direction === "in" ? "+" : "-"}
          {formatMoney(card.amount, currency)}
        </span>
      </button>
    );
  }

  const payable = card.direction === "out" && PAYABLE.has(card.node.type);
  const paid = payable && isPaid(card.node, month);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpen(card.node)}
        className={`flex w-full items-center gap-2 rounded-xl border border-l-4 border-node-border bg-node-bg px-3 py-2 text-left shadow-lg transition-transform hover:-translate-y-0.5 hover:border-accent ${
          paid ? "opacity-60" : ""
        }`}
        style={{ borderLeftColor: TYPE_COLOUR[card.node.type] }}
      >
        <Icon name={TYPE_ICON[card.node.type]} size={16} className={ICON_COLOUR[card.node.type]} />
        <div className="min-w-0 flex-1 pr-5">
          <p className="truncate text-sm text-ink">{card.label}</p>
          <p
            className={`figure text-sm ${card.direction === "in" ? "text-positive" : "text-accent"} ${
              paid ? "line-through" : ""
            }`}
          >
            {card.direction === "in" ? "+" : "-"}
            {formatMoney(card.amount, currency)}
          </p>
        </div>
      </button>
      {payable ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            togglePaid(card.node.id);
          }}
          onPointerDown={(event) => event.stopPropagation()}
          aria-pressed={paid}
          title={paid ? "Paid this month" : "Mark as paid"}
          className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border text-[11px] leading-none transition-colors ${
            paid
              ? "border-positive bg-positive text-canvas"
              : "border-node-border text-transparent hover:border-positive hover:text-positive"
          }`}
        >
          ✓
        </button>
      ) : null}
    </div>
  );
}

function Summary({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "positive" | "accent" | "warning" | "ink";
}) {
  const colour =
    tone === "positive"
      ? "text-positive"
      : tone === "accent"
        ? "text-accent"
        : tone === "warning"
          ? "text-warning"
          : "text-ink";
  return (
    <div className="rounded-xl border border-node-border bg-node-bg px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-ink-muted">{label}</p>
      <p className={`figure mt-1 text-lg ${colour}`}>{value}</p>
    </div>
  );
}
